import React, { useEffect, useMemo, useState } from "react";

export default function Carousel({ images = [], autoMs = 4500 }) {
  const safeImages = useMemo(() => images.filter(Boolean), [images]);
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (safeImages.length <= 1 || open) return; // pause autoplay when modal is open
    const t = setInterval(() => setIdx((i) => (i + 1) % safeImages.length), autoMs);
    return () => clearInterval(t);
  }, [safeImages.length, autoMs, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + safeImages.length) % safeImages.length);
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % safeImages.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, safeImages.length]);

  if (!safeImages.length) return null;

  const prev = () => setIdx((i) => (i - 1 + safeImages.length) % safeImages.length);
  const next = () => setIdx((i) => (i + 1) % safeImages.length);

  return (
    <>
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5">
        <div className="relative aspect-[16/10] w-full bg-black/20">
          {safeImages.map((src, i) => (
            <button
              key={src + i}
              type="button"
              onClick={() => setOpen(true)}
              className={[
                "absolute inset-0 h-full w-full transition-opacity duration-700",
                i === idx ? "opacity-100" : "opacity-0 pointer-events-none",
              ].join(" ")}
              aria-label="Open image"
            >
              {/* object-contain to fit vertical photos */}
              <img
                src={src}
                alt={`BMW X4 M Competition ${i + 1}`}
                className="h-full w-full object-contain"
                loading={i === idx ? "eager" : "lazy"}
              />
            </button>
          ))}

          {/* soft overlay */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/35 via-transparent to-transparent" />
        </div>

        {safeImages.length > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              aria-label="Previous"
              className="absolute left-3 top-1/2 -translate-y-1/2 rounded-2xl bg-black/35 px-3 py-2 text-white/90 ring-1 ring-white/15 hover:bg-black/45 transition"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next"
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-2xl bg-black/35 px-3 py-2 text-white/90 ring-1 ring-white/15 hover:bg-black/45 transition"
            >
              ›
            </button>

            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 rounded-full bg-black/30 px-3 py-2 ring-1 ring-white/10">
              {safeImages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={[
                    "h-2 w-2 rounded-full transition",
                    i === idx ? "bg-white" : "bg-white/30 hover:bg-white/50",
                  ].join(" ")}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-[999] grid place-items-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setOpen(false)}
        >
          <div
            className="relative w-full max-w-5xl overflow-hidden rounded-3xl border border-white/10 bg-slate-950/60 backdrop-blur-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 md:px-5">
              <div className="text-sm text-white/70">
                Снимка {idx + 1} от {safeImages.length}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-2xl bg-white/10 px-3 py-1.5 text-sm font-semibold text-white ring-1 ring-white/15 hover:bg-white/15 transition"
              >
                Close ×
              </button>
            </div>

            <div className="relative bg-black/30">
              <img
                src={safeImages[idx]}
                alt={`BMW X4 M Competition large ${idx + 1}`}
                className="max-h-[80vh] w-full object-contain"
              />

              {safeImages.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prev}
                    aria-label="Previous in modal"
                    className="absolute left-3 top-1/2 -translate-y-1/2 rounded-2xl bg-black/40 px-3 py-2 text-white/90 ring-1 ring-white/15 hover:bg-black/55 transition"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={next}
                    aria-label="Next in modal"
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-2xl bg-black/40 px-3 py-2 text-white/90 ring-1 ring-white/15 hover:bg-black/55 transition"
                  >
                    ›
                  </button>
                </>
              )}
            </div>

            <div className="px-4 py-3 text-xs text-white/60 md:px-5">
              Tip: ESC to close • ← / → for next/prev
            </div>
          </div>
        </div>
      )}
    </>
  );
}
